// The arithmetic behind the Asset Management summary. Pure functions over the
// registry, allocation and maintenance records, so the screen only lays the
// answers out. Money and units run through the same quantity rules as the
// registry tiles, so the two never disagree.

import { dateOf, lastMonths, recent, daysUntil, inYear, sumOf, totalBy } from '../../utils/summaryStats';
import type { MonthBucket, NamedTotal } from '../../utils/summaryStats';
import { calculateCurrentValue } from './depreciation';
import { unitsOf, totalCostOf, isSponsored } from './quantity';

export interface AssetLike {
  recordId?: string;
  name?: string;
  category?: string;
  brand?: string;
  purchaseDate?: string;
  purchaseCost?: unknown;
  depreciationRate?: unknown;
  quantity?: unknown;
  /** PURCHASED / SPONSORED; blank on old rows, which count as purchased. */
  acquisitionType?: string;
  sponsorName?: string;
  warrantyExpiry?: string;
  location?: string;
  department?: string;
}

export interface AllocationLike {
  assetId?: string;
  date?: string;
  custodian?: string;
  newLocation?: string;
  newDepartment?: string;
  purpose?: string;
}

export interface MaintenanceLike {
  assetId?: string;
  date?: string;
  vendor?: string;
  problem?: string;
  cost?: unknown;
  nextServiceDate?: string;
}

/** One group of the registry: rows, units, what they cost and are worth now. */
export interface AssetGroup {
  name: string;
  rows: number;
  units: number;
  cost: number;
  book: number;
}

/** An asset whose next service (from its latest maintenance entry) is near or past. */
export interface ServiceDue {
  assetId: string;
  name: string;
  location: string;
  vendor: string;
  nextServiceDate: string;
  /** Days until the service; negative when overdue. */
  days: number;
}

export interface WarrantyDue {
  assetId: string;
  name: string;
  category: string;
  warrantyExpiry: string;
  days: number;
}

export interface AssetSummary {
  rows: number;
  units: number;
  totalCost: number;
  purchasedCost: number;
  sponsoredValue: number;
  bookValue: number;
  depreciated: number;
  sources: AssetGroup[];
  byCategory: AssetGroup[];
  byLocation: AssetGroup[];
  byDepartment: AssetGroup[];
  maintTotal: number;
  maintThisYear: number;
  maintMonths: MonthBucket[];
  /** Spend per asset, largest first, named from the registry. */
  maintByAsset: NamedTotal[];
  serviceOverdue: ServiceDue[];
  serviceSoon: ServiceDue[];
  underWarranty: number;
  warrantySoon: WarrantyDue[];
  transfers: AllocationLike[];
}

/** How far ahead the warranty watch-list looks. */
export const WARRANTY_WINDOW = 60;

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Book value of every unit on a row, as of `now`. */
export const bookValueOf = (a: AssetLike, now: Date = new Date()): number =>
  calculateCurrentValue(a.purchaseCost, a.purchaseDate, a.depreciationRate, now).current * unitsOf(a);

/** The registry grouped by a field, largest cost first; a blank value is "Not set". */
export const groupAssets = (assets: AssetLike[], pick: (a: AssetLike) => unknown, now: Date = new Date()): AssetGroup[] => {
  const map = new Map<string, AssetGroup>();
  assets.forEach((a) => {
    const name = String(pick(a) ?? '').trim() || 'Not set';
    const g = map.get(name) || { name, rows: 0, units: 0, cost: 0, book: 0 };
    g.rows += 1;
    g.units += unitsOf(a);
    g.cost += totalCostOf(a);
    g.book += bookValueOf(a, now);
    map.set(name, g);
  });
  return Array.from(map.values())
    .map((g) => ({ ...g, cost: round2(g.cost), book: round2(g.book) }))
    .sort((a, b) => b.cost - a.cost || b.units - a.units || a.name.localeCompare(b.name));
};

export const assetSummary = (
  assets: AssetLike[], allocations: AllocationLike[], maintenance: MaintenanceLike[], now: Date = new Date()
): AssetSummary => {
  const byId = new Map(assets.map((a) => [String(a.recordId || ''), a]));
  const nameOf = (id?: string) => byId.get(String(id || ''))?.name || id || 'Unknown asset';

  const totalCost = round2(sumOf(assets, totalCostOf));
  const sponsoredValue = round2(sumOf(assets.filter((a) => isSponsored(a)), totalCostOf));
  const bookValue = round2(assets.reduce((s, a) => s + bookValueOf(a, now), 0));

  // Only an asset's latest maintenance entry says when it is next due; an
  // older entry's date has been superseded by the visit that followed it.
  // Entries for assets no longer in the registry are left out.
  const latest = new Map<string, MaintenanceLike>();
  maintenance.forEach((m) => {
    const id = String(m.assetId || '');
    if (!byId.has(id)) return;
    const cur = latest.get(id);
    if (!cur || String(dateOf(m.date) || '') >= String(dateOf(cur.date) || '')) latest.set(id, m);
  });
  const service: ServiceDue[] = [];
  latest.forEach((m, id) => {
    const days = daysUntil(m.nextServiceDate, now);
    if (days === null || days > 30) return;
    const a = byId.get(id);
    service.push({
      assetId: id, name: nameOf(id), location: a?.location || '', vendor: m.vendor || '',
      nextServiceDate: String(dateOf(m.nextServiceDate)), days
    });
  });
  service.sort((a, b) => a.days - b.days);

  const warranties = assets
    .map((a) => ({ a, days: daysUntil(a.warrantyExpiry, now) }))
    .filter((x): x is { a: AssetLike; days: number } => x.days !== null && x.days >= 0);

  return {
    rows: assets.length,
    units: assets.reduce((s, a) => s + unitsOf(a), 0),
    totalCost,
    purchasedCost: round2(totalCost - sponsoredValue),
    sponsoredValue,
    bookValue,
    depreciated: round2(totalCost - bookValue),
    sources: groupAssets(assets, (a) => (isSponsored(a) ? 'Sponsored / Gifted' : 'Purchased by church'), now),
    byCategory: groupAssets(assets, (a) => a.category, now),
    byLocation: groupAssets(assets, (a) => a.location, now),
    byDepartment: groupAssets(assets, (a) => a.department, now),
    maintTotal: sumOf(maintenance, (m) => m.cost),
    maintThisYear: sumOf(inYear(maintenance, (m) => m.date, now.getFullYear()), (m) => m.cost),
    maintMonths: lastMonths(maintenance, (m) => m.date, 12, now, (m) => m.cost),
    maintByAsset: totalBy(maintenance, (m) => nameOf(m.assetId), (m) => m.cost),
    serviceOverdue: service.filter((s) => s.days < 0),
    serviceSoon: service.filter((s) => s.days >= 0),
    underWarranty: warranties.length,
    warrantySoon: warranties
      .filter((x) => x.days <= WARRANTY_WINDOW)
      .sort((x, y) => x.days - y.days)
      .map(({ a, days }) => ({
        assetId: a.recordId || '', name: a.name || '-', category: a.category || '',
        warrantyExpiry: String(dateOf(a.warrantyExpiry)), days
      })),
    transfers: recent(allocations, (x) => x.date, 90, now)
  };
};
